/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  AdminTokenResponse,
  AppConfigEntry,
  AppConfigValueInput,
  AuditLogEntry,
  BillingPlan,
  BillingPlanInput,
  BillingSubscription,
  BillingSubscriptionInput,
  BillingSubscriptionStatusInput,
  DemoDataRequest,
  DemoDataResponse,
  Error,
  GetAuditLogParams,
  GetJmxMetrics200,
  HealthResponse,
  InstrumentSyncRequest,
  InstrumentSyncRun,
  SupportTicket,
  SupportTicketCreate,
} from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Creates a new support ticket from a user contact request
 * @summary Create support ticket
 */
export const createSupportTicket = (
  supportTicketCreate: SupportTicketCreate,
  signal?: AbortSignal
) => {
  return customInstance<SupportTicket>({
    url: `/api/admin/support/tickets`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: supportTicketCreate,
    signal,
  });
};

export const getCreateSupportTicketMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSupportTicket>>,
    TError,
    { data: SupportTicketCreate },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSupportTicket>>,
  TError,
  { data: SupportTicketCreate },
  TContext
> => {
  const mutationKey = ['createSupportTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSupportTicket>>,
    { data: SupportTicketCreate }
  > = (props) => {
    const { data } = props ?? {};

    return createSupportTicket(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSupportTicketMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSupportTicket>>
>;
export type CreateSupportTicketMutationBody = SupportTicketCreate;
export type CreateSupportTicketMutationError = Error;

/**
 * @summary Create support ticket
 */
export const useCreateSupportTicket = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSupportTicket>>,
      TError,
      { data: SupportTicketCreate },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSupportTicket>>,
  TError,
  { data: SupportTicketCreate },
  TContext
> => {
  const mutationOptions = getCreateSupportTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Generates comprehensive demo data including accounts, transactions, goals, and documents for testing purposes
 * @summary Generate demo data
 */
export const generateDemoData = (demoDataRequest?: DemoDataRequest, signal?: AbortSignal) => {
  return customInstance<DemoDataResponse>({
    url: `/api/admin/demo`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: demoDataRequest,
    signal,
  });
};

export const getGenerateDemoDataMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateDemoData>>,
    TError,
    { data: DemoDataRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateDemoData>>,
  TError,
  { data: DemoDataRequest },
  TContext
> => {
  const mutationKey = ['generateDemoData'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateDemoData>>,
    { data: DemoDataRequest }
  > = (props) => {
    const { data } = props ?? {};

    return generateDemoData(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateDemoDataMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateDemoData>>
>;
export type GenerateDemoDataMutationBody = DemoDataRequest;
export type GenerateDemoDataMutationError = Error;

/**
 * @summary Generate demo data
 */
export const useGenerateDemoData = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateDemoData>>,
      TError,
      { data: DemoDataRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof generateDemoData>>,
  TError,
  { data: DemoDataRequest },
  TContext
> => {
  const mutationOptions = getGenerateDemoDataMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Starts an instrument catalog sync from one provider (or all configured providers when omitted).
 * @summary Trigger instrument universe sync
 */
export const syncInstruments = (
  instrumentSyncRequest?: InstrumentSyncRequest,
  signal?: AbortSignal
) => {
  return customInstance<InstrumentSyncRun[]>({
    url: `/api/admin/market/instruments/sync`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: instrumentSyncRequest,
    signal,
  });
};

export const getSyncInstrumentsMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof syncInstruments>>,
    TError,
    { data: InstrumentSyncRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof syncInstruments>>,
  TError,
  { data: InstrumentSyncRequest },
  TContext
> => {
  const mutationKey = ['syncInstruments'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof syncInstruments>>,
    { data: InstrumentSyncRequest }
  > = (props) => {
    const { data } = props ?? {};

    return syncInstruments(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SyncInstrumentsMutationResult = NonNullable<
  Awaited<ReturnType<typeof syncInstruments>>
>;
export type SyncInstrumentsMutationBody = InstrumentSyncRequest;
export type SyncInstrumentsMutationError = Error;

/**
 * @summary Trigger instrument universe sync
 */
export const useSyncInstruments = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof syncInstruments>>,
      TError,
      { data: InstrumentSyncRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof syncInstruments>>,
  TError,
  { data: InstrumentSyncRequest },
  TContext
> => {
  const mutationOptions = getSyncInstrumentsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary Latest instrument sync runs per provider
 */
export const getInstrumentSyncStatus = (signal?: AbortSignal) => {
  return customInstance<InstrumentSyncRun[]>({
    url: `/api/admin/market/instruments/sync-status`,
    method: 'GET',
    signal,
  });
};

export const getGetInstrumentSyncStatusQueryKey = () => {
  return [`/api/admin/market/instruments/sync-status`] as const;
};

export const getGetInstrumentSyncStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getInstrumentSyncStatus>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInstrumentSyncStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getInstrumentSyncStatus>>> = ({
    signal,
  }) => getInstrumentSyncStatus(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInstrumentSyncStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getInstrumentSyncStatus>>
>;
export type GetInstrumentSyncStatusQueryError = Error;

export function useGetInstrumentSyncStatus<
  TData = Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInstrumentSyncStatus>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
          TError,
          Awaited<ReturnType<typeof getInstrumentSyncStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInstrumentSyncStatus<
  TData = Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInstrumentSyncStatus>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
          TError,
          Awaited<ReturnType<typeof getInstrumentSyncStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInstrumentSyncStatus<
  TData = Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInstrumentSyncStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Latest instrument sync runs per provider
 */

export function useGetInstrumentSyncStatus<
  TData = Awaited<ReturnType<typeof getInstrumentSyncStatus>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInstrumentSyncStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetInstrumentSyncStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns the health status of the application and database
 * @summary Health check endpoint
 */
export const healthCheck = (signal?: AbortSignal) => {
  return customInstance<HealthResponse>({ url: `/health`, method: 'GET', signal });
};

export const getHealthCheckQueryKey = () => {
  return [`/health`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({ signal }) =>
    healthCheck(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type HealthCheckQueryResult = NonNullable<Awaited<ReturnType<typeof healthCheck>>>;
export type HealthCheckQueryError = unknown;

export function useHealthCheck<TData = Awaited<ReturnType<typeof healthCheck>>, TError = unknown>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof healthCheck>>,
          TError,
          Awaited<ReturnType<typeof healthCheck>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useHealthCheck<TData = Awaited<ReturnType<typeof healthCheck>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof healthCheck>>,
          TError,
          Awaited<ReturnType<typeof healthCheck>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useHealthCheck<TData = Awaited<ReturnType<typeof healthCheck>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Health check endpoint
 */

export function useHealthCheck<TData = Awaited<ReturnType<typeof healthCheck>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns Prometheus metrics for monitoring
 * @summary Prometheus metrics
 */
export const getMetrics = (signal?: AbortSignal) => {
  return customInstance<string>({ url: `/api/admin/system/metrics`, method: 'GET', signal });
};

export const getGetMetricsQueryKey = () => {
  return [`/api/admin/system/metrics`] as const;
};

export const getGetMetricsQueryOptions = <
  TData = Awaited<ReturnType<typeof getMetrics>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMetrics>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMetricsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMetrics>>> = ({ signal }) =>
    getMetrics(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMetrics>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMetricsQueryResult = NonNullable<Awaited<ReturnType<typeof getMetrics>>>;
export type GetMetricsQueryError = unknown;

export function useGetMetrics<TData = Awaited<ReturnType<typeof getMetrics>>, TError = unknown>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMetrics>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMetrics>>,
          TError,
          Awaited<ReturnType<typeof getMetrics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMetrics<TData = Awaited<ReturnType<typeof getMetrics>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMetrics>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMetrics>>,
          TError,
          Awaited<ReturnType<typeof getMetrics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMetrics<TData = Awaited<ReturnType<typeof getMetrics>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMetrics>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Prometheus metrics
 */

export function useGetMetrics<TData = Awaited<ReturnType<typeof getMetrics>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMetrics>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMetricsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns JMX metrics
 * @summary JMX metrics
 */
export const getJmxMetrics = (signal?: AbortSignal) => {
  return customInstance<GetJmxMetrics200>({
    url: `/api/admin/system/metrics/jmx`,
    method: 'GET',
    signal,
  });
};

export const getGetJmxMetricsQueryKey = () => {
  return [`/api/admin/system/metrics/jmx`] as const;
};

export const getGetJmxMetricsQueryOptions = <
  TData = Awaited<ReturnType<typeof getJmxMetrics>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJmxMetrics>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetJmxMetricsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getJmxMetrics>>> = ({ signal }) =>
    getJmxMetrics(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getJmxMetrics>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetJmxMetricsQueryResult = NonNullable<Awaited<ReturnType<typeof getJmxMetrics>>>;
export type GetJmxMetricsQueryError = unknown;

export function useGetJmxMetrics<
  TData = Awaited<ReturnType<typeof getJmxMetrics>>,
  TError = unknown,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJmxMetrics>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getJmxMetrics>>,
          TError,
          Awaited<ReturnType<typeof getJmxMetrics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetJmxMetrics<
  TData = Awaited<ReturnType<typeof getJmxMetrics>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJmxMetrics>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getJmxMetrics>>,
          TError,
          Awaited<ReturnType<typeof getJmxMetrics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetJmxMetrics<
  TData = Awaited<ReturnType<typeof getJmxMetrics>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJmxMetrics>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary JMX metrics
 */

export function useGetJmxMetrics<
  TData = Awaited<ReturnType<typeof getJmxMetrics>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJmxMetrics>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetJmxMetricsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Mints a short-lived (default 1 hour) admin-scoped token for use by external tooling (e.g. an ad hoc ops dashboard) instead of a long-lived browser session token. Callable only by a caller already on the admin allowlist (SECURITY.md P0-2).
 * @summary Issue a short-lived admin API token
 */
export const issueAdminToken = (signal?: AbortSignal) => {
  return customInstance<AdminTokenResponse>({ url: `/api/admin/token`, method: 'POST', signal });
};

export const getIssueAdminTokenMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof issueAdminToken>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<Awaited<ReturnType<typeof issueAdminToken>>, TError, void, TContext> => {
  const mutationKey = ['issueAdminToken'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof issueAdminToken>>, void> = () => {
    return issueAdminToken();
  };

  return { mutationFn, ...mutationOptions };
};

export type IssueAdminTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof issueAdminToken>>
>;

export type IssueAdminTokenMutationError = Error;

/**
 * @summary Issue a short-lived admin API token
 */
export const useIssueAdminToken = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof issueAdminToken>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof issueAdminToken>>, TError, void, TContext> => {
  const mutationOptions = getIssueAdminTokenMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Append-only log of admin actions and other sensitive events (SECURITY.md P1-2).
 * @summary Query the admin audit log
 */
export const getAuditLog = (params?: GetAuditLogParams, signal?: AbortSignal) => {
  return customInstance<AuditLogEntry[]>({
    url: `/api/admin/audit-log`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetAuditLogQueryKey = (params?: GetAuditLogParams) => {
  return [`/api/admin/audit-log`, ...(params ? [params] : [])] as const;
};

export const getGetAuditLogQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuditLog>>,
  TError = Error,
>(
  params?: GetAuditLogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuditLog>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAuditLogQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuditLog>>> = ({ signal }) =>
    getAuditLog(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuditLog>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAuditLogQueryResult = NonNullable<Awaited<ReturnType<typeof getAuditLog>>>;
export type GetAuditLogQueryError = Error;

export function useGetAuditLog<TData = Awaited<ReturnType<typeof getAuditLog>>, TError = Error>(
  params: undefined | GetAuditLogParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuditLog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuditLog>>,
          TError,
          Awaited<ReturnType<typeof getAuditLog>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAuditLog<TData = Awaited<ReturnType<typeof getAuditLog>>, TError = Error>(
  params?: GetAuditLogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuditLog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuditLog>>,
          TError,
          Awaited<ReturnType<typeof getAuditLog>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAuditLog<TData = Awaited<ReturnType<typeof getAuditLog>>, TError = Error>(
  params?: GetAuditLogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuditLog>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Query the admin audit log
 */

export function useGetAuditLog<TData = Awaited<ReturnType<typeof getAuditLog>>, TError = Error>(
  params?: GetAuditLogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuditLog>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAuditLogQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Runtime-toggleable config (feature flags, pricing, maintenance banners) without a redeploy. Admin-only.
 * @summary List runtime app config entries
 */
export const getAppConfig = (signal?: AbortSignal) => {
  return customInstance<AppConfigEntry[]>({ url: `/api/admin/config`, method: 'GET', signal });
};

export const getGetAppConfigQueryKey = () => {
  return [`/api/admin/config`] as const;
};

export const getGetAppConfigQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppConfig>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAppConfig>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAppConfigQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAppConfig>>> = ({ signal }) =>
    getAppConfig(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAppConfig>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAppConfigQueryResult = NonNullable<Awaited<ReturnType<typeof getAppConfig>>>;
export type GetAppConfigQueryError = Error;

export function useGetAppConfig<TData = Awaited<ReturnType<typeof getAppConfig>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAppConfig>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAppConfig>>,
          TError,
          Awaited<ReturnType<typeof getAppConfig>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAppConfig<TData = Awaited<ReturnType<typeof getAppConfig>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAppConfig>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAppConfig>>,
          TError,
          Awaited<ReturnType<typeof getAppConfig>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAppConfig<TData = Awaited<ReturnType<typeof getAppConfig>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAppConfig>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List runtime app config entries
 */

export function useGetAppConfig<TData = Awaited<ReturnType<typeof getAppConfig>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAppConfig>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAppConfigQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Set a runtime app config value
 */
export const setAppConfig = (key: string, appConfigValueInput: AppConfigValueInput) => {
  return customInstance<AppConfigEntry>({
    url: `/api/admin/config/${key}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: appConfigValueInput,
  });
};

export const getSetAppConfigMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setAppConfig>>,
    TError,
    { key: string; data: AppConfigValueInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setAppConfig>>,
  TError,
  { key: string; data: AppConfigValueInput },
  TContext
> => {
  const mutationKey = ['setAppConfig'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setAppConfig>>,
    { key: string; data: AppConfigValueInput }
  > = (props) => {
    const { key, data } = props ?? {};

    return setAppConfig(key, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetAppConfigMutationResult = NonNullable<Awaited<ReturnType<typeof setAppConfig>>>;
export type SetAppConfigMutationBody = AppConfigValueInput;
export type SetAppConfigMutationError = Error;

/**
 * @summary Set a runtime app config value
 */
export const useSetAppConfig = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setAppConfig>>,
      TError,
      { key: string; data: AppConfigValueInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setAppConfig>>,
  TError,
  { key: string; data: AppConfigValueInput },
  TContext
> => {
  const mutationOptions = getSetAppConfigMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary List billing plans
 */
export const listBillingPlans = (signal?: AbortSignal) => {
  return customInstance<BillingPlan[]>({ url: `/api/admin/billing/plans`, method: 'GET', signal });
};

export const getListBillingPlansQueryKey = () => {
  return [`/api/admin/billing/plans`] as const;
};

export const getListBillingPlansQueryOptions = <
  TData = Awaited<ReturnType<typeof listBillingPlans>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listBillingPlans>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBillingPlansQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listBillingPlans>>> = ({ signal }) =>
    listBillingPlans(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listBillingPlans>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListBillingPlansQueryResult = NonNullable<Awaited<ReturnType<typeof listBillingPlans>>>;
export type ListBillingPlansQueryError = unknown;

export function useListBillingPlans<
  TData = Awaited<ReturnType<typeof listBillingPlans>>,
  TError = unknown,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listBillingPlans>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBillingPlans>>,
          TError,
          Awaited<ReturnType<typeof listBillingPlans>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListBillingPlans<
  TData = Awaited<ReturnType<typeof listBillingPlans>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listBillingPlans>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBillingPlans>>,
          TError,
          Awaited<ReturnType<typeof listBillingPlans>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListBillingPlans<
  TData = Awaited<ReturnType<typeof listBillingPlans>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listBillingPlans>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List billing plans
 */

export function useListBillingPlans<
  TData = Awaited<ReturnType<typeof listBillingPlans>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listBillingPlans>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListBillingPlansQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create or update a billing plan
 */
export const upsertBillingPlan = (billingPlanInput: BillingPlanInput, signal?: AbortSignal) => {
  return customInstance<BillingPlan>({
    url: `/api/admin/billing/plans`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: billingPlanInput,
    signal,
  });
};

export const getUpsertBillingPlanMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upsertBillingPlan>>,
    TError,
    { data: BillingPlanInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof upsertBillingPlan>>,
  TError,
  { data: BillingPlanInput },
  TContext
> => {
  const mutationKey = ['upsertBillingPlan'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof upsertBillingPlan>>,
    { data: BillingPlanInput }
  > = (props) => {
    const { data } = props ?? {};

    return upsertBillingPlan(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpsertBillingPlanMutationResult = NonNullable<
  Awaited<ReturnType<typeof upsertBillingPlan>>
>;
export type UpsertBillingPlanMutationBody = BillingPlanInput;
export type UpsertBillingPlanMutationError = Error;

/**
 * @summary Create or update a billing plan
 */
export const useUpsertBillingPlan = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof upsertBillingPlan>>,
      TError,
      { data: BillingPlanInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof upsertBillingPlan>>,
  TError,
  { data: BillingPlanInput },
  TContext
> => {
  const mutationOptions = getUpsertBillingPlanMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary List billing subscriptions
 */
export const listBillingSubscriptions = (signal?: AbortSignal) => {
  return customInstance<BillingSubscription[]>({
    url: `/api/admin/billing/subscriptions`,
    method: 'GET',
    signal,
  });
};

export const getListBillingSubscriptionsQueryKey = () => {
  return [`/api/admin/billing/subscriptions`] as const;
};

export const getListBillingSubscriptionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listBillingSubscriptions>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof listBillingSubscriptions>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBillingSubscriptionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listBillingSubscriptions>>> = ({
    signal,
  }) => listBillingSubscriptions(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listBillingSubscriptions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListBillingSubscriptionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listBillingSubscriptions>>
>;
export type ListBillingSubscriptionsQueryError = unknown;

export function useListBillingSubscriptions<
  TData = Awaited<ReturnType<typeof listBillingSubscriptions>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBillingSubscriptions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBillingSubscriptions>>,
          TError,
          Awaited<ReturnType<typeof listBillingSubscriptions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListBillingSubscriptions<
  TData = Awaited<ReturnType<typeof listBillingSubscriptions>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBillingSubscriptions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBillingSubscriptions>>,
          TError,
          Awaited<ReturnType<typeof listBillingSubscriptions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListBillingSubscriptions<
  TData = Awaited<ReturnType<typeof listBillingSubscriptions>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBillingSubscriptions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List billing subscriptions
 */

export function useListBillingSubscriptions<
  TData = Awaited<ReturnType<typeof listBillingSubscriptions>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBillingSubscriptions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListBillingSubscriptionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Assign a plan to an organization (manual, no live payment)
 */
export const createBillingSubscription = (
  billingSubscriptionInput: BillingSubscriptionInput,
  signal?: AbortSignal
) => {
  return customInstance<BillingSubscription>({
    url: `/api/admin/billing/subscriptions`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: billingSubscriptionInput,
    signal,
  });
};

export const getCreateBillingSubscriptionMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBillingSubscription>>,
    TError,
    { data: BillingSubscriptionInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createBillingSubscription>>,
  TError,
  { data: BillingSubscriptionInput },
  TContext
> => {
  const mutationKey = ['createBillingSubscription'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createBillingSubscription>>,
    { data: BillingSubscriptionInput }
  > = (props) => {
    const { data } = props ?? {};

    return createBillingSubscription(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateBillingSubscriptionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createBillingSubscription>>
>;
export type CreateBillingSubscriptionMutationBody = BillingSubscriptionInput;
export type CreateBillingSubscriptionMutationError = Error;

/**
 * @summary Assign a plan to an organization (manual, no live payment)
 */
export const useCreateBillingSubscription = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createBillingSubscription>>,
      TError,
      { data: BillingSubscriptionInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createBillingSubscription>>,
  TError,
  { data: BillingSubscriptionInput },
  TContext
> => {
  const mutationOptions = getCreateBillingSubscriptionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary Update a subscription's status
 */
export const updateBillingSubscriptionStatus = (
  id: string,
  billingSubscriptionStatusInput: BillingSubscriptionStatusInput
) => {
  return customInstance<BillingSubscription>({
    url: `/api/admin/billing/subscriptions/${id}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: billingSubscriptionStatusInput,
  });
};

export const getUpdateBillingSubscriptionStatusMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>,
    TError,
    { id: string; data: BillingSubscriptionStatusInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>,
  TError,
  { id: string; data: BillingSubscriptionStatusInput },
  TContext
> => {
  const mutationKey = ['updateBillingSubscriptionStatus'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>,
    { id: string; data: BillingSubscriptionStatusInput }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateBillingSubscriptionStatus(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBillingSubscriptionStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>
>;
export type UpdateBillingSubscriptionStatusMutationBody = BillingSubscriptionStatusInput;
export type UpdateBillingSubscriptionStatusMutationError = Error;

/**
 * @summary Update a subscription's status
 */
export const useUpdateBillingSubscriptionStatus = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>,
      TError,
      { id: string; data: BillingSubscriptionStatusInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateBillingSubscriptionStatus>>,
  TError,
  { id: string; data: BillingSubscriptionStatusInput },
  TContext
> => {
  const mutationOptions = getUpdateBillingSubscriptionStatusMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns the OpenAPI specification in YAML format
 * @summary OpenAPI specification
 */
export const getOpenApiSpec = (signal?: AbortSignal) => {
  return customInstance<string>({ url: `/api/admin/system/openapi`, method: 'GET', signal });
};

export const getGetOpenApiSpecQueryKey = () => {
  return [`/api/admin/system/openapi`] as const;
};

export const getGetOpenApiSpecQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpenApiSpec>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOpenApiSpec>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOpenApiSpecQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpenApiSpec>>> = ({ signal }) =>
    getOpenApiSpec(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOpenApiSpec>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOpenApiSpecQueryResult = NonNullable<Awaited<ReturnType<typeof getOpenApiSpec>>>;
export type GetOpenApiSpecQueryError = unknown;

export function useGetOpenApiSpec<
  TData = Awaited<ReturnType<typeof getOpenApiSpec>>,
  TError = unknown,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOpenApiSpec>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOpenApiSpec>>,
          TError,
          Awaited<ReturnType<typeof getOpenApiSpec>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetOpenApiSpec<
  TData = Awaited<ReturnType<typeof getOpenApiSpec>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOpenApiSpec>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOpenApiSpec>>,
          TError,
          Awaited<ReturnType<typeof getOpenApiSpec>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetOpenApiSpec<
  TData = Awaited<ReturnType<typeof getOpenApiSpec>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOpenApiSpec>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary OpenAPI specification
 */

export function useGetOpenApiSpec<
  TData = Awaited<ReturnType<typeof getOpenApiSpec>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getOpenApiSpec>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetOpenApiSpecQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
